// Prefer Next.js rewrite proxy to avoid CORS in dev
// Be robust if env is empty or just '/'
const rawBase = (process.env.NEXT_PUBLIC_API_URL || '').trim();
export const API_BASE = (rawBase && rawBase !== '/' ? rawBase : '/api/backend').replace(/\/$/, '');
// Simple absolute/base URL used by lightweight endpoints below
const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

// Try to ensure we have a stable user id in localStorage
export async function ensureUserId(): Promise<string | undefined> {
  // Already cached
  try {
    const existing = (typeof window !== 'undefined') ? localStorage.getItem('user_id') : undefined;
    if (existing) return existing;
  } catch {}
  // Try decode JWT
  try {
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('auth_token');
      if (token && token.split('.').length >= 2) {
        const payloadRaw = token.split('.')[1];
        const json = JSON.parse(atob(payloadRaw.replace(/-/g, '+').replace(/_/g, '/')));
        const sub = json?.sub || json?.id || json?.user_id || json?.userId;
        if (sub != null) {
          const uid = String(sub);
          localStorage.setItem('user_id', uid);
          return uid;
        }
      }
    }
  } catch {}
  // Try to fetch user profile from common endpoints
  const candidates = [
    '/auth/me', '/api/auth/me', '/me', '/api/me',
    '/users/me', '/api/users/me', '/user/me', '/api/user/me',
    '/profile', '/api/profile', '/auth/user', '/api/auth/user'
  ];
  for (const p of candidates) {
    try {
      const data: any = await doFetch(p, { method: 'GET' });
      const uid = data?.id ?? data?.user?.id ?? data?.user_id ?? data?.userId;
      if (uid != null) {
        const val = String(uid);
        try { if (typeof window !== 'undefined') localStorage.setItem('user_id', val); } catch {}
        return val;
      }
    } catch {}
  }
  return undefined;
}

function getClientUserId(): string | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    const ls = localStorage as Storage;
    const explicit = ls.getItem('user_id');
    // Only use user_id if it's explicitly set, don't fall back to email/name/token
    // as these aren't proper user identifiers
    return explicit || undefined;
  } catch {
    return undefined;
  }
}

// Lightweight logger: only logs in development to avoid lint/build failures
function devLogError(...args: any[]) {
  try {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn?.(...(args as any));
    }
  } catch {}
}

async function doFetch(path: string, init: RequestInit = {}) {
  // Attach Bearer token and x-user-id from localStorage when on client
  const authHeader: Record<string, string> = {};
  try {
    if (typeof window !== 'undefined') {
      const t = localStorage.getItem('auth_token');
      if (t) authHeader['Authorization'] = `Bearer ${t}`;
      const uid = getClientUserId();
      if (uid) {
        authHeader['x-user-id'] = uid;
        authHeader['x-user'] = uid;
      }
    }
  } catch {}
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...authHeader,
      ...(init.headers || {}),
    },
    ...init,
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const data = await res.clone().json();
      message = data?.message || data?.error || message;
    } catch (_) {
      try {
        const text = await res.text();
        const looksHtml = /<\s*!doctype|<\s*html[\s>]/i.test(text || '');
        message = looksHtml ? `HTTP ${res.status}` : text.slice(0, 800);
      } catch { /* noop */ }
    }
    devLogError('API error', { path, status: res.status, message });
    throw new Error(message);
  }
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : res.text();
}

// Local API fetcher (bypass API_BASE proxy) for Next routes under /api/*
async function doLocal(path: string, init: RequestInit = {}) {
  const authHeader: Record<string, string> = {};
  try {
    if (typeof window !== 'undefined') {
      const t = localStorage.getItem('auth_token');
      if (t) authHeader['Authorization'] = `Bearer ${t}`;
      const uid = getClientUserId();
      if (uid) {
        authHeader['x-user-id'] = uid;
        authHeader['x-user'] = uid;
      }
    }
  } catch {}
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...authHeader,
      ...(init.headers || {}),
    },
    ...init,
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const data = await res.clone().json();
      message = (data as any)?.message || (data as any)?.error || message;
    } catch {
      try { message = await res.text(); } catch {}
    }
    throw new Error(message);
  }
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : res.text();
}

// ---- Agents & Knowledge Collections (DB-backed) ----

export async function listAgents() {
  function normalizeArray(payload: any): any[] {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.items)) return payload.items;
    if (payload && Array.isArray(payload.data)) return payload.data;
    return [];
  }
  try {
    // First try to use local API route with auth headers via doLocal
    const dataLocal = await doLocal('/api/agents', { method: 'GET' });
    const arrLocal = normalizeArray(dataLocal);
    if (arrLocal.length > 0) return arrLocal;
  } catch {}
  try {
    // If local fails, try backend
    const paths = ['/agents','/db/agents','/api/agents','/chat/agents','/db/chat/agents','/api/chat/agents'];
    for (const p of paths) {
      try {
        const data = await doFetch(p, { method: 'GET' });
        const arr = normalizeArray(data);
        if (arr.length > 0) return arr;
      } catch { /* try next */ }
    }
  } catch {}
  try {
    // Last-chance direct call to configured absolute API_URL
    const authHeader: Record<string, string> = {};
    try {
      if (typeof window !== 'undefined') {
        const t = localStorage.getItem('auth_token');
        if (t) authHeader['Authorization'] = `Bearer ${t}`;
        const uid = (localStorage.getItem('user_id') || undefined);
        if (uid) { authHeader['x-user-id'] = uid; authHeader['x-user'] = uid; }
      }
    } catch {}
    const res = await fetch(`${API_URL.replace(/\/$/, '')}/agents`, { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json', ...authHeader } });
    if (res.ok) {
      const data = await res.json();
      return normalizeArray(data);
    }
  } catch {}
  // Fallback to empty array if API fails
  return [];
}

export async function createAgentRecord(payload: any) {
  // Normalize field names for various backends
  const body: any = {
    name: payload?.name,
    provider: payload?.provider,
    model: payload?.model,
    description: payload?.description,
    // send both keys so different backends accept either
    system: payload?.system ?? payload?.system_prompt,
    system_prompt: payload?.system_prompt ?? payload?.system,
    // icon/avatar variants
    icon_url: payload?.icon_url ?? payload?.iconUrl ?? payload?.avatar ?? payload?.icon,
    avatar: payload?.avatar ?? payload?.icon_url ?? payload?.iconUrl ?? payload?.icon,
    // visibility/active variants
    is_public: payload?.is_public ?? payload?.is_active ?? true,
    is_active: payload?.is_active ?? payload?.is_public ?? true,
    // timestamps for backends that require client-provided values
    created_at: payload?.created_at ?? new Date().toISOString(),
    createdAt: payload?.createdAt ?? new Date().toISOString(),
    updated_at: payload?.updated_at ?? new Date().toISOString(),
    updatedAt: payload?.updatedAt ?? new Date().toISOString(),
  };
  try {
    // First try to use local API route with auth headers so x-user-id is sent
    const data = await doLocal('/api/agents', { method: 'POST', body: JSON.stringify(body) });
    return data;
  } catch {}
  try {
    // If local fails, try backend via proxy base (with auth headers)
    return await doFetch('/agents', { method: 'POST', body: JSON.stringify(body) });
  } catch (err) {
    // Last resort: try backend again
    return await doFetch('/agents', { method: 'POST', body: JSON.stringify(body) });
  }
}

export async function deleteAgentRecord(id: string | number) {
  try {
    // First try to use local API route
    const res = await fetch(`/api/agents/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (res.ok) {
      return await res.json();
    }
    // If local fails, try backend
    return await doFetch(`/agents/${id}`, { method: 'DELETE' });
  } catch (err) {
    // Last resort: try backend
    return await doFetch(`/agents/${id}`, { method: 'DELETE' });
  }
}

export async function getAgentById(id: string | number) {
  function pick(obj: any): any {
    if (!obj) return obj;
    // Some backends wrap in { item } or { data }
    if (obj.item) return obj.item;
    if (obj.data && !Array.isArray(obj.data)) return obj.data;
    return obj;
  }
  // Try local
  try {
    const data = await doLocal(`/api/agents/${id}`, { method: 'GET' });
    return pick(data);
  } catch {}
  // Try backend aliases
  const paths = [`/agents/${id}`, `/db/agents/${id}`, `/api/agents/${id}`, `/chat/agents/${id}`, `/db/chat/agents/${id}`, `/api/chat/agents/${id}`];
  for (const p of paths) {
    try { const data = await doFetch(p, { method: 'GET' }); return pick(data); } catch { /* next */ }
  }
  // Last-chance absolute
  try {
    const res = await fetch(`${API_URL.replace(/\/$/, '')}/agents/${id}`, { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
    if (res.ok) return pick(await res.json());
  } catch {}
  throw new Error('Agent not found');
}

// Use only legacy/actual `knowledge` endpoints from now on
export async function listKnowledgeCollections() {
  // This function now lists from `/knowledge` only, normalizing common shapes
  const normalizeArray = (payload: any): any[] => {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.items)) return payload.items;
    if (payload && Array.isArray(payload.data)) return payload.data;
    return [];
  };
  // Prefer local API route so we read from our DB store when available
  try {
    const dataLocal = await doLocal('/api/knowledge', { method: 'GET' });
    const arrLocal = normalizeArray(dataLocal);
    if (arrLocal.length > 0) {
      return arrLocal.map((x: any) => {
        if (x && typeof x === 'object') {
          const id = x.knowledge_id ?? x.id ?? x.knowledgeId ?? x.uuid;
          const name = x.name ?? x.title;
          return { id: id != null ? String(id) : undefined, name, title: x.title, description: x.description, icon: x.icon_url ?? x.icon, ...x };
        }
        return { id: String(x), name: String(x) };
      }).filter((x: any) => x.id);
    }
  } catch {}
  try {
    const data = await doFetch('/knowledge', { method: 'GET' });
    const arr = normalizeArray(data);
    return arr.map((x: any) => {
      if (x && typeof x === 'object') {
        const id = x.knowledge_id ?? x.id ?? x.knowledgeId ?? x.uuid;
        const name = x.name ?? x.title;
        return { id: id != null ? String(id) : undefined, name, title: x.title, description: x.description, icon: x.icon_url ?? x.icon, ...x };
      }
      return { id: String(x), name: String(x) };
    }).filter((x: any) => x.id);
  } catch {}
  return [];
}

export async function createKnowledgeCollection(payload: { name: string; description?: string; items?: any[] }) {
  // Create a knowledge record, then patch title/description
  const id = await createKnowledge();
  try {
    const title = payload?.name;
    const description = payload?.description;
    if (title || description) {
      await patchKnowledge(id, { title, description });
    }
  } catch {}
  return { id, name: payload?.name ?? id, description: payload?.description } as any;
}

export async function deleteKnowledgeCollection(id: string | number) {
  return await deleteKnowledge(String(id));
}

// Delete legacy/actual knowledge record (table `knowledge`)
export async function deleteKnowledge(id: string) {
  if (!id || String(id).trim() === '') {
    throw new Error('knowledge id required');
  }
  // Try local Next route first
  try {
    const res = await fetch(`/api/knowledge/${encodeURIComponent(String(id))}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (res.ok) return await res.json().catch(() => ({}));
  } catch {}
  // Try alternate mount
  try {
    const res = await fetch(`/knowledge/${encodeURIComponent(String(id))}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (res.ok) return await res.json().catch(() => ({}));
  } catch {}
  // Fallback via proxy base (try common mounts)
  try { return await doFetch(`/knowledge/${encodeURIComponent(String(id))}`, { method: 'DELETE' }); } catch {}
  // Some backends mount under /db/knowledge
  return await doFetch(`/db/knowledge/${encodeURIComponent(String(id))}`, { method: 'DELETE' });
}

// ---- Knowledge (instruction_knowledge + file_text) ----

// Create empty knowledge and return its id (knowledge_id)
export async function createKnowledge(): Promise<string> {
  // Prefer local Next route so we send x-user-id to set created_by
  try {
    const dataLocal: any = await doLocal('/api/knowledge', { method: 'POST' });
    const idLocal = String(dataLocal?.knowledge_id ?? dataLocal?.id ?? dataLocal?.knowledgeId ?? dataLocal?.uuid ?? dataLocal);
    if (idLocal) return idLocal;
  } catch {}
  const paths = ['/knowledge', '/api/backend/knowledge', '/api/backend/api/knowledge', '/db/knowledge', '/api/backend/db/knowledge'];
  for (const p of paths) {
    try {
      const data: any = await doFetch(p, { method: 'POST' });
      const id = String(data?.knowledge_id ?? data?.id ?? data?.knowledgeId ?? data?.uuid ?? data);
      if (id) return id;
    } catch {}
  }
  // Last resort: use proxy base
  const data = await doFetch('/knowledge', { method: 'POST' });
  const id = String((data as any)?.knowledge_id ?? (data as any)?.id ?? (data as any)?.knowledgeId ?? data);
  if (id) return id;
  throw new Error('Unable to create knowledge');
}

// Patch instruction_knowledge (and optionally title/description if backend accepts)
export async function patchKnowledge(
  id: string,
  patch: {
    system_prompt?: string;
    title?: string;
    description?: string;
    temperature?: number;
    icon?: string;
  }
) {
  // Support both 'system_prompt' (new) and 'instruction_knowledge' (legacy) to be safe
  const payload: any = { ...patch };
  if (payload.instruction_knowledge && !payload.system_prompt) {
    payload.system_prompt = payload.instruction_knowledge;
  }
  // Map icon field variants for different backends
  if (payload.icon && !payload.icon_url) payload.icon_url = payload.icon;
  const tries = [
    `/api/knowledge/${encodeURIComponent(String(id))}`,
    `/knowledge/${encodeURIComponent(String(id))}`,
  ];
  for (const p of tries) {
    try { return await doLocal(p, { method: 'PATCH', body: JSON.stringify(payload) }); } catch {}
  }
  return await doFetch(`/knowledge/${encodeURIComponent(String(id))}`, { method: 'PATCH', body: JSON.stringify(payload) });
}

// Upload a file into knowledge; expects multipart key 'file'
export async function uploadKnowledgeFile(id: string, file: File): Promise<any> {
  const fd = new FormData();
  fd.append('file', file, file.name);
  const localPaths = [
    `/api/knowledge/upload/${encodeURIComponent(String(id))}`,
    `/knowledge/upload/${encodeURIComponent(String(id))}`,
  ];
  for (const p of localPaths) {
    try {
      const res = await fetch(p, { method: 'POST', body: fd, credentials: 'include' });
      if (res.ok) return await res.json().catch(() => ({}));
    } catch {}
  }
  // Fallback through proxy base
  const res = await fetch(`${API_BASE}/knowledge/upload/${encodeURIComponent(String(id))}`, { method: 'POST', body: fd as any, credentials: 'include' });
  if (!res.ok) throw new Error(await res.text());
  return res.json().catch(() => ({}));
}

// Quick ask endpoint for direct Q&A without opening UI conversation
export async function askKnowledge(id: string, question: string): Promise<any> {
  const payload = { question };
  const tries = [
    `/api/knowledge/${encodeURIComponent(String(id))}/ask`,
    `/knowledge/${encodeURIComponent(String(id))}/ask`,
  ];
  for (const p of tries) {
    try { return await doLocal(p, { method: 'POST', body: JSON.stringify(payload) }); } catch {}
  }
  return await doFetch(`/knowledge/${encodeURIComponent(String(id))}/ask`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function ensureCsrf() {
  // Sets XSRF-TOKEN cookie via backend
  await fetch(`${API_BASE}/csrf/token`, { credentials: 'include' });
}

export async function createSession(
  title?: string,
  opts?: { provider?: string; model?: string }
): Promise<{ id: number; title?: string }>{
  const uid = getClientUserId();
  const payload: any = { title, ...(opts || {}) };
  if (uid) {
    payload.user_id = uid;
    payload.userId = uid;
    payload.owner_id = uid;
    payload.ownerId = uid;
    payload.created_by = uid;
    payload.createdBy = uid;
    payload.user = uid;
  }
  try { await ensureCsrf(); } catch {}
  // First try local Next route so we can set knowledge_id cookie and patch response
  try {
    const raw = await doLocal('/api/conversations', { method: 'POST', body: JSON.stringify(payload) });
    try {
      if (raw && typeof raw === 'object') {
        const id = Number((raw as any).id ?? (raw as any).session_id ?? (raw as any).sessionId ?? (raw as any).data?.id ?? (raw as any).session?.id ?? (Array.isArray((raw as any).items) ? (raw as any).items?.[0]?.id : undefined) ?? (Array.isArray(raw) ? (raw as any)[0]?.id : undefined));
        if (Number.isFinite(id) && id > 0) {
          const titleOut = (raw as any).title ?? (raw as any).data?.title ?? undefined;
          return { id, title: titleOut };
        }
      }
      if (typeof raw === 'string') {
        try {
          const obj = JSON.parse(raw);
          if (obj && typeof obj === 'object') {
            const id2 = Number((obj as any).id ?? (obj as any).session_id ?? (obj as any).sessionId ?? (obj as any).data?.id);
            if (Number.isFinite(id2) && id2 > 0) return { id: id2, title: (obj as any).title ?? (obj as any).data?.title };
          }
        } catch {}
        const m = (raw as string).match(/\b(\d{1,})\b/);
        if (m) {
          const id3 = Number(m[1]);
          if (Number.isFinite(id3) && id3 > 0) return { id: id3 };
        }
      }
    } catch {}
    return raw as any;
  } catch {}
  const basePaths = [
    // Prefer Conversations endpoints so explore_type/agent/knowledge are honored
    '/api/conversations',
    '/conversations',
    '/api/chat/conversations',
    '/chat/conversations',
    // Legacy session endpoints as fallback
    '/api/chat/sessions',
    '/chat/sessions',
    '/api/sessions',
    '/sessions',
    '/db/sessions',
  ];
  const tries = (() => {
    if (!uid) return basePaths;
    const qp1 = `?user_id=${encodeURIComponent(String(uid))}`;
    const qp2 = `?userId=${encodeURIComponent(String(uid))}`;
    const qp3 = `?owner_id=${encodeURIComponent(String(uid))}`;
    const qp4 = `?ownerId=${encodeURIComponent(String(uid))}`;
    const qp5 = `?created_by=${encodeURIComponent(String(uid))}`;
    const qp6 = `?createdBy=${encodeURIComponent(String(uid))}`;
    const out: string[] = [];
    for (const p of basePaths) {
      out.push(p, `${p}${qp1}`, `${p}${qp2}`, `${p}${qp3}`, `${p}${qp4}`, `${p}${qp5}`, `${p}${qp6}`);
    }
    return out;
  })();
  let lastErr: any;
  for (const p of tries) {
    try {
      const raw = await doFetch(p, { method: 'POST', body: JSON.stringify(payload) });
      try {
        if (raw && typeof raw === 'object') {
          const id = Number((raw as any).id ?? (raw as any).session_id ?? (raw as any).sessionId ?? (raw as any).data?.id ?? (raw as any).session?.id ?? (Array.isArray((raw as any).items) ? (raw as any).items?.[0]?.id : undefined) ?? (Array.isArray(raw) ? (raw as any)[0]?.id : undefined));
          if (Number.isFinite(id) && id > 0) {
            const titleOut = (raw as any).title ?? (raw as any).data?.title ?? undefined;
            try {
              if (typeof window !== 'undefined') {
                const ls = localStorage.getItem('my_session_ids');
                const arr = ls ? JSON.parse(ls) : [];
                const next = Array.from(new Set([id, ...((Array.isArray(arr) ? arr : []) as any)]));
                localStorage.setItem('my_session_ids', JSON.stringify(next));
                try { await fetch('/api/sessions/claim', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) }); } catch {}
              }
            } catch {}
            return { id, title: titleOut };
          }
        }
        if (typeof raw === 'string') {
          try {
            const obj = JSON.parse(raw);
            if (obj && typeof obj === 'object') {
              const id2 = Number((obj as any).id ?? (obj as any).session_id ?? (obj as any).sessionId ?? (obj as any).data?.id);
              if (Number.isFinite(id2) && id2 > 0) return { id: id2, title: (obj as any).title ?? (obj as any).data?.title };
            }
          } catch {}
          const m = (raw as string).match(/\b(\d{1,})\b/);
          if (m) {
            const id3 = Number(m[1]);
            if (Number.isFinite(id3) && id3 > 0) return { id: id3 };
          }
        }
      } catch {}
      return raw as any;
    } catch (err) { lastErr = err; }
  }
  throw lastErr || new Error('Unable to create session');
}

import { extractUsage, logUsage } from '@/lib/usage';

export async function sendChatMessage(
  sessionId: number,
  message: string,
  modelChoice: any,
  conversationId?: number | string
) {
  const conv = conversationId ?? sessionId;
  const qp = `?conversation_id=${encodeURIComponent(String(conv))}`;
  const uid = getClientUserId();
  const payload = {
    // Ensure backend can associate message with its conversation (multiple aliases)
    conversation_id: conv,
    conversationId: conv,
    conversationID: conv,
    session_id: sessionId,
    message,
    provider: modelChoice.provider,
    model: modelChoice.model,
    // Always include user_id if available
    ...(uid ? {
      user_id: uid,
      userId: uid,
      owner_id: uid,
      ownerId: uid,
      created_by: uid,
      createdBy: uid,
      user: uid,
    } : {}),
  } as any;
  // Attach agent metadata (system + knowledge) tied to this session
  try {
    if (typeof window !== 'undefined') {
      // If user previously selected a Knowledge/Agent context, include identifiers as message-level hints
      try {
        const ak = localStorage.getItem('active_knowledge_id');
        if (ak) {
          (payload as any).explore_type = 'knowledge';
          (payload as any).explore_name = ak;
          (payload as any).knowledge_id = ak;
          // Fetch knowledge details to inject system_prompt/file_text if backend doesn't do it
          try {
            const rec: any = await doLocal(`/api/knowledge/${encodeURIComponent(String(ak))}`, { method: 'GET' });
            if (rec) {
              if (!((payload as any).system_prompt) && (rec.system_prompt || rec.system)) {
                const sp = rec.system_prompt || rec.system;
                (payload as any).system = sp;
                (payload as any).system_prompt = sp;
              }
              if (rec.file_text && typeof rec.file_text === 'string' && rec.file_text.trim()) {
                const arr = ((payload as any).knowledge_texts || []) as string[];
                arr.push(rec.file_text);
                (payload as any).knowledge_texts = arr;
              }
            }
          } catch {}
        }
        const aa = localStorage.getItem('active_agent_id');
        if (!ak && aa) {
          (payload as any).explore_type = 'agent';
          (payload as any).explore_name = aa;
          (payload as any).agent_id = Number.isFinite(Number(aa)) ? Number(aa) : aa as any;
        }
      } catch {}
      const sys = localStorage.getItem(`agent_system:${sessionId}`);
      if (sys) {
        (payload as any).system = sys;
        (payload as any).system_prompt = sys;
      }
      const knRaw = localStorage.getItem(`agent_knowledge:${sessionId}`);
      if (knRaw) {
        try {
          const items = JSON.parse(knRaw) as any[];
          const urls = items.filter((x: any) => x?.url).map((x: any) => x.url);
          const texts = items.filter((x: any) => x?.text).map((x: any) => x.text);
          if (urls.length) (payload as any).knowledge_urls = urls;
          if (texts.length) (payload as any).knowledge_texts = texts;
          // If no explicit system prompt was set but knowledge is attached, enable strict knowledge mode
          if (!sys && (urls.length || texts.length)) {
            const strict = [
              'โหมดตอบจากแหล่งความรู้เท่านั้น (STRICT):',
              '1) ตอบเฉพาะข้อมูลที่พบในไฟล์/ลิงก์ที่แนบ',
              "2) หากไม่พบ ให้ตอบว่า 'ไม่พบข้อมูลในเอกสาร' และขอข้อมูลเพิ่ม",
              '3) ห้ามเดาหรือยกตัวอย่างนอกเอกสาร',
              '4) สรุปสั้น กระชับ และระบุอ้างอิงสั้นๆ (เช่นชื่อไฟล์/หัวข้อ/หน้า) เมื่ออธิบาย',
              'ภาษาที่ใช้: ภาษาไทย',
            ].join('\n');
            (payload as any).system = strict;
            (payload as any).system_prompt = strict;
            (payload as any).knowledge_only = true;
            (payload as any).citations = true;
          }
        } catch {}
      }
    }
  } catch {}
  // Backend assembles system/knowledge; strip any locally-injected hints
  try {
    delete (payload as any).system;
    delete (payload as any).system_prompt;
    delete (payload as any).knowledge_texts;
    delete (payload as any).knowledge_urls;
    // Keep explore_type/explore_name/knowledge_id/agent_id as hints for server composer
    delete (payload as any).knowledge_only;
    delete (payload as any).citations;
  } catch {}
  // Try multiple mounts to be backend-agnostic
  const cid = encodeURIComponent(String(conv));
  // First try local route so server can compose system + knowledge
  try {
    const data = await doLocal(`/api/conversations/${cid}/messages`, { method: 'POST', body: JSON.stringify(payload) });
    try {
      const usage = extractUsage(data);
      if (usage) {
        logUsage(usage, {
          provider: modelChoice?.provider,
          model: modelChoice?.model,
          sessionId,
        });
      }
    } catch {}
    return data;
  } catch {}
  const paths = [
    // Conversations-first endpoints
    `/conversations/${cid}/messages`,
    `/api/conversations/${cid}/messages`,
    `/chat/conversations/${cid}/messages`,
    `/api/chat/conversations/${cid}/messages`,
    // Legacy session/message endpoints with conversation_id fallback
    `/chat/sessions/${sessionId}/messages${qp}`,
    `/api/chat/sessions/${sessionId}/messages${qp}`,
    `/chat/messages${qp}`,
    `/api/chat/messages${qp}`,
    `/messages${qp}`,
  ];
  let lastErr: any;
  for (const p of paths) {
    try {
      const res = await doFetch(p, { method: 'POST', body: JSON.stringify(payload) });
      try {
        const usage = extractUsage(res);
        if (usage) {
          logUsage(usage, {
            provider: modelChoice?.provider,
            model: modelChoice?.model,
            sessionId,
            conversationId: conv,
            source: 'client',
          });
        }
      } catch {}
      return res;
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr || new Error('Failed to send message');
}

// Verify that the current authenticated user has access to a session/conversation id
export async function verifySessionAccess(id: number | string): Promise<boolean> {
  const sid = encodeURIComponent(String(id));
  // quick allow if we know we created this id
  try {
    if (typeof window !== 'undefined') {
      const active = localStorage.getItem('active_session_id');
      if (active && String(active) === String(id)) return true;
      const ls = localStorage.getItem('my_session_ids');
      const arr = ls ? JSON.parse(ls) : [];
      if (Array.isArray(arr) && arr.includes(Number(id))) return true;
    }
  } catch {}
  // ask local guard store first
  try {
    const res = await fetch(`/api/sessions/check?id=${sid}`, { credentials: 'include' });
    if (res.ok) {
      const j = await res.json();
      if (j?.allowed === true) return true;
      if (j?.allowed === false) return false;
    }
  } catch {}
  const paths = [
    `/chat/sessions/${sid}`,
    `/api/chat/sessions/${sid}`,
    `/sessions/${sid}`,
    `/api/sessions/${sid}`,
    `/chat/conversations/${sid}`,
    `/api/chat/conversations/${sid}`,
    `/conversations/${sid}`,
    `/api/conversations/${sid}`,
  ];
  for (const p of paths) {
    try {
      const res = await doFetch(p, { method: 'GET' });
      if (res) return true;
    } catch (err: any) {
      // try other variants; if backend returns 403/404, continue and ultimately deny
    }
  }
  return false;
}

// Send a chat message with file attachment via multipart/form-data
export async function sendChatMessageWithFile(
  sessionId: number,
  message: string,
  modelChoice: any,
  file: File,
  conversationId?: number | string
) {
  const fd = new FormData();
  if (message) fd.append('message', message);
  if (modelChoice?.provider) fd.append('provider', modelChoice.provider);
  if (modelChoice?.model) fd.append('model', modelChoice.model);
  // Include multiple keys for compatibility with different backends
  const conv = (conversationId ?? sessionId);
  fd.append('conversation_id', String(conv));
  fd.append('conversationId', String(conv));
  fd.append('conversationID', String(conv));
  fd.append('session_id', String(sessionId));
  const uid = getClientUserId();
  // Always include user_id if available
  if (uid) {
    fd.append('user_id', String(uid));
    fd.append('userId', String(uid));
    fd.append('owner_id', String(uid));
    fd.append('ownerId', String(uid));
    fd.append('created_by', String(uid));
    fd.append('createdBy', String(uid));
    fd.append('user', String(uid));
  }
  // Attach the file under several common field names
  fd.append('file', file, file.name);
  fd.append('image', file, file.name);
  fd.append('attachment', file, file.name);
  fd.append('upload', file, file.name);
  // Include system prompt if set for this session
  try {
    if (typeof window !== 'undefined') {
      // Include knowledge/agent identifiers as message-level hints
      try {
        const ak = localStorage.getItem('active_knowledge_id');
        if (ak) {
          fd.append('explore_type', 'knowledge');
          fd.append('explore_name', ak);
          fd.append('knowledge_id', ak);
          // Fetch knowledge details to inject system_prompt/file_text if backend doesn't do it
          try {
            const rec: any = await doLocal(`/api/knowledge/${encodeURIComponent(String(ak))}`, { method: 'GET' });
            if (rec) {
              if (rec.system_prompt) {
                const hasSys = (fd as any).has && ((fd as any).has('system') || (fd as any).has('system_prompt'));
                if (!hasSys) { fd.append('system', rec.system_prompt); fd.append('system_prompt', rec.system_prompt); }
              }
              if (rec.file_text) { fd.append('knowledge_texts', rec.file_text); }
            }
          } catch {}
        }
        const aa = localStorage.getItem('active_agent_id');
        if (!ak && aa) {
          fd.append('explore_type', 'agent');
          fd.append('explore_name', aa);
          fd.append('agent_id', String(Number.isFinite(Number(aa)) ? Number(aa) : aa));
        }
      } catch {}
      const sys = localStorage.getItem(`agent_system:${sessionId}`);
      if (sys) { fd.append('system', sys); fd.append('system_prompt', sys); }
      const knRaw = localStorage.getItem(`agent_knowledge:${sessionId}`);
      if (!sys && knRaw) {
        try {
          const items = JSON.parse(knRaw) as any[];
          if (Array.isArray(items) && items.length) {
            const strict = [
              'โหมดตอบจากแหล่งความรู้เท่านั้น (STRICT):',
              '1) ตอบเฉพาะข้อมูลที่พบในไฟล์/ลิงก์ที่แนบ',
              "2) หากไม่พบ ให้ตอบว่า 'ไม่พบข้อมูลในเอกสาร' และขอข้อมูลเพิ่ม",
              '3) ห้ามเดาหรือยกตัวอย่างนอกเอกสาร',
              '4) สรุปสั้น กระชับ และระบุอ้างอิงสั้นๆ (เช่นชื่อไฟล์/หัวข้อ/หน้า) เมื่ออธิบาย',
              'ภาษาที่ใช้: ภาษาไทย',
            ].join('\n');
            fd.append('system', strict);
            fd.append('system_prompt', strict);
            fd.append('knowledge_only', 'true');
            fd.append('citations', 'true');
          }
        } catch {}
      }
    }
  } catch {}
  // Helpful metadata that some backends require
  try {
    fd.append('filename', file.name);
    fd.append('content_type', (file as any).type || 'application/octet-stream');
    fd.append('mime', (file as any).type || 'application/octet-stream');
  } catch {}
  
  // Backend assembles system/knowledge; remove any locally-injected hints on the form
  try {
    (fd as any).delete?.('system');
    (fd as any).delete?.('system_prompt');
    (fd as any).delete?.('knowledge_texts');
    (fd as any).delete?.('knowledge_urls');
    // keep explore/knowledge hints for server composer
    (fd as any).delete?.('knowledge_only');
    (fd as any).delete?.('citations');
  } catch {}
  // Prefer Next.js rewrite proxy for uploads to avoid CORS with credentials
  const qp = `?conversation_id=${encodeURIComponent(String(conv))}`;
  // First try local route so server can compose system + knowledge
  try {
    const url = `/api/conversations/${encodeURIComponent(String(conv))}/messages`;
    const res = await fetch(url, { method: 'POST', body: fd as any, credentials: 'include' });
    if (res.ok) return (await res.json().catch(()=>({}))) as any;
  } catch {}
  const paths = [
    // Canonical
    `/api/chat/sessions/${sessionId}/messages${qp}`,
    // Alternate message upload endpoints
    `/api/chat/messages${qp}`,
    `/api/messages${qp}`,
    `/api/chat/messages/upload${qp}`,
    `/api/messages/upload${qp}`,
    // Generic upload endpoints
    `/chat/uploads${qp}`,
    `/api/chat/uploads${qp}`,
    `/uploads${qp}`,
    `/api/uploads${qp}`,
    `/files${qp}`,
    `/api/files${qp}`,
  ];
  // Force using the local rewrite base for the first attempts
  const bases = ['/api/backend', API_BASE];
  let lastErr: any;
  for (const base of bases) {
    for (const p of paths) {
      try {
        const url = `${base}${p}`.replace(/\/+$/, '');
        // Attach auth header when available (browser only)
        let upHeaders: any = { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
        try {
          if (typeof window !== 'undefined') {
            const token = localStorage.getItem('auth_token');
            if (token) upHeaders['Authorization'] = `Bearer ${token}`;
            const uid2 = getClientUserId();
            if (uid2) upHeaders['x-user-id'] = String(uid2);
          }
        } catch {}
        const res = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: upHeaders,
          body: fd,
        });
        if (!res.ok) {
          let text = '';
          try { text = await res.text(); } catch {}
          lastErr = new Error(`Upload ${res.status}: ${text || 'unknown'}`);
          continue;
        }
        const ct = res.headers.get('content-type') || '';
        const parsed = ct.includes('application/json') ? await res.json() : await res.text();
        try {
          const usage = extractUsage(parsed);
          if (usage) {
            logUsage(usage, {
              provider: modelChoice?.provider,
              model: modelChoice?.model,
              sessionId,
              conversationId: conv,
              source: 'client-upload',
            });
          }
        } catch {}
        return parsed;
      } catch (err) {
        lastErr = err;
      }
    }
  }
  throw lastErr || new Error('Failed to send message');
}


export async function listSessions() {
  // Try multiple mounts to be backend-agnostic
  const tries = [
    '/chat/sessions',
    '/api/chat/sessions',
    '/chat/conversations',
    '/api/chat/conversations',
    '/sessions',
    '/api/sessions',
    '/db/sessions',
  ];
  let lastErr: any;
  for (const p of tries) {
    try { return await doFetch(p, { method: 'GET' }); } catch (err) { lastErr = err; }
  }
  // Be resilient: if no known endpoint exists, return empty list instead of crashing UI
  if (process.env.NODE_ENV !== 'production') {
    try { console.warn?.('listSessions: falling back to empty list', String(lastErr?.message || '')); } catch {}
  }
  return [] as any[];
}

export async function getSession(id: number) {
  const tries = [
    `/chat/sessions/${id}`,
    `/api/chat/sessions/${id}`,
    `/sessions/${id}`,
    `/api/sessions/${id}`,
  ];
  let lastErr: any;
  for (const p of tries) {
    try { return await doFetch(p, { method: 'GET' }); } catch (err) { lastErr = err; }
  }
  throw lastErr || new Error('Unable to fetch session');
}

// List messages for a session
export async function listMessages(sessionId: number) {
  const tries = [
    `/chat/sessions/${sessionId}/messages`,
    `/api/chat/sessions/${sessionId}/messages`,
  ];
  let lastErr: any;
  for (const p of tries) {
    try { return await doFetch(p, { method: 'GET' }); } catch (err) { lastErr = err; }
  }
  // Be permissive: if backend doesn't support listing yet, return empty
  return [] as any[];
}

// List messages by conversation id with multiple fallbacks
export async function listMessagesByConversation(conversationId: number | string) {
  const cid = encodeURIComponent(String(conversationId));
  const tries = [
    `/chat/conversations/${cid}/messages`,
    `/api/chat/conversations/${cid}/messages`,
    `/chat/sessions/${cid}/messages`,
    `/api/chat/sessions/${cid}/messages`,
    `/chat/messages?conversation_id=${cid}`,
    `/api/chat/messages?conversation_id=${cid}`,
    `/messages?conversation_id=${cid}`,
    `/api/messages?conversation_id=${cid}`,
    `/db/messages?conversation_id=${cid}`,
  ];
  let lastErr: any;
  for (const path of tries) {
    try {
      return await doFetch(path, { method: 'GET' });
    } catch (err) {
      lastErr = err;
    }
  }
  // If none matched, return empty to avoid blocking UI
  return [] as any[];
}

// Update conversation provider/model
export async function updateSession(id: number, data: any) {
  const tries = [
    `/chat/sessions/${id}`,
    `/api/chat/sessions/${id}`,
    `/sessions/${id}`,
    `/api/sessions/${id}`,
  ];
  let lastErr: any;
  for (const p of tries) {
    try { return await doFetch(p, { method: 'PATCH', body: JSON.stringify(data) }); } catch (err) { lastErr = err; }
  }
  throw lastErr || new Error('Unable to update session');
}

// Update conversation metadata (fallback across mounts)
export async function updateConversation(id: number | string, data: any) {
  // Ensure user_id is included if available
  const uid = getClientUserId();
  const payload = { ...data };
  if (uid) {
    payload.user_id = uid;
    payload.userId = uid;
    payload.owner_id = uid;
    payload.ownerId = uid;
    payload.created_by = uid;
    payload.createdBy = uid;
  }
  
  const tries = [
    `/chat/conversations/${id}`,
    `/api/chat/conversations/${id}`,
    `/conversations/${id}`,
    `/api/conversations/${id}`,
  ];
  let lastErr: any;
  for (const p of tries) {
    try { return await doFetch(p, { method: 'PATCH', body: JSON.stringify(payload) }); } catch (err) { lastErr = err; }
  }
  throw lastErr || new Error('Unable to update conversation');
}

// Delete a chat session
export async function deleteSession(id: number) {
  // Try canonical REST delete first
  try {
    await doFetch(`/chat/sessions/${id}`, { method: 'DELETE' });
    return { ok: true };
  } catch (err: any) {
    const msg = String(err?.message || '');
    // If the backend does not support DELETE (404/405), try fallbacks
    const methodUnsupported = /cannot\s+delete|405|not\s+allowed|not\s+found|404/i.test(msg);
    if (!methodUnsupported) throw err;
  }

  // Fallback A: soft delete via PATCH
  try {
    await doFetch(`/chat/sessions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ archived: true, deleted: true }),
    });
    return { ok: true };
  } catch {}

  // Fallback B: POST to an explicit delete endpoint
  try {
    await doFetch(`/chat/sessions/${id}/delete`, { method: 'POST' });
    return { ok: true };
  } catch {}

  // Fallback C: POST to a collection delete
  try {
    await doFetch(`/chat/sessions/delete`, {
      method: 'POST',
      body: JSON.stringify({ id }),
    });
    return { ok: true };
  } catch (err) {
    throw err;
  }
}

// Search sessions (by title and message content) via Next.js API route
export async function searchSessions(term: string) {
  const params = new URLSearchParams();
  const q = (term || '').trim();
  if (q) params.set('q', q);
  const res = await fetch(`/api/chat/sessions/search?${params.toString()}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    try {
      const data = await res.clone().json();
      throw new Error(data?.message || 'Search failed');
    } catch {
      try { throw new Error(await res.text()); } catch { throw new Error('Search failed'); }
    }
  }
  return res.json();
}

// Search sessions by matching messages.content; returns sessions grouped by conversation_id
export async function searchSessionsByMessage(term: string) {
  const params = new URLSearchParams();
  const q = (term || '').trim();
  if (q) params.set('q', q);
  const res = await fetch(`/api/chat/sessions/find-by-message?${params.toString()}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    try {
      const data = await res.clone().json();
      throw new Error(data?.message || 'Search failed');
    } catch {
      try { throw new Error(await res.text()); } catch { throw new Error('Search failed'); }
    }
  }
  return res.json();
}


// Auth
export async function registerUser(payload: {
  first_name?: string;
  last_name?: string;
  email: string;
  password: string;
}) {
  await ensureCsrf().catch(() => {});
  const res = await fetch(`${API_BASE}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function loginUser(payload: { email: string; password: string }) {
  await ensureCsrf().catch(() => {});
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    credentials: 'include',
  });

  if (!res.ok) {
    let message = 'Login failed';
    try {
      const data = await res.json();
      message = data?.message || message;
    } catch {}
    throw new Error(message);
  }

  return res.json();
}

// Persist user avatar URL to backend database using common endpoints/keys
export async function saveUserAvatar(avatarUrl: string) {
  const uid = getClientUserId();
  const payload: any = {
    avatar: avatarUrl,
    avatar_url: avatarUrl,
    avatarUrl: avatarUrl,
    profile_image: avatarUrl,
    profile_image_url: avatarUrl,
    profileImageUrl: avatarUrl,
  };
  if (uid) payload.user_id = uid;
  const configured = (process.env.NEXT_PUBLIC_PROFILE_UPDATE_PATH || '').trim();
  const tries = [
    ...(configured ? [{ path: configured, method: 'PATCH' as const }] : []),
    { path: '/auth/profile', method: 'PATCH' },
    { path: '/api/auth/profile', method: 'PATCH' },
    { path: '/profile', method: 'PATCH' },
    { path: '/api/profile', method: 'PATCH' },
    { path: '/users/me', method: 'PATCH' },
    { path: '/api/users/me', method: 'PATCH' },
    { path: '/user/me', method: 'PATCH' },
    { path: '/api/user/me', method: 'PATCH' },
    { path: '/auth/me', method: 'PATCH' },
    { path: '/api/auth/me', method: 'PATCH' },
  ];
  let lastErr: any;
  for (const t of tries) {
    try {
      await doFetch(t.path, { method: t.method as any, body: JSON.stringify(payload) });
      return { ok: true };
    } catch (err) { lastErr = err; }
  }
  // As a last resort, try POST as some backends only allow POST
  for (const t of tries) {
    try {
      await doFetch(t.path, { method: 'POST', body: JSON.stringify(payload) });
      return { ok: true };
    } catch (err) { lastErr = err; }
  }
  throw lastErr || new Error('Unable to save avatar');
}

// Persist basic profile fields to backend database
export async function saveUserProfile(patch: { /* User profile properties removed as per requirements */ }) {
  const uid = getClientUserId();
  const payload: any = {};
  if (uid) payload.user_id = uid;
  const configured = (process.env.NEXT_PUBLIC_PROFILE_UPDATE_PATH || '').trim();
  const tries = [
    ...(configured ? [{ path: configured, method: 'PATCH' as const }] : []),
    { path: '/auth/profile', method: 'PATCH' },
    { path: '/api/auth/profile', method: 'PATCH' },
    { path: '/profile', method: 'PATCH' },
    { path: '/api/profile', method: 'PATCH' },
    { path: '/users/me', method: 'PATCH' },
    { path: '/api/users/me', method: 'PATCH' },
    { path: '/auth/me', method: 'PATCH' },
    { path: '/api/auth/me', method: 'PATCH' },
  ];
  let lastErr: any;
  for (const t of tries) {
    try {
      await doFetch(t.path, { method: t.method as any, body: JSON.stringify(payload) });
      return { ok: true };
    } catch (err) { lastErr = err; }
  }
  for (const t of tries) {
    try {
      await doFetch(t.path, { method: 'POST', body: JSON.stringify(payload) });
      return { ok: true };
    } catch (err) { lastErr = err; }
  }
  throw lastErr || new Error('Unable to save profile');
}

